<script setup>
import { __, getFormValuesFromLocalStorage, requestPlanCTP, checkIfOnlyEsim, requestRegistrationFormFields, decodeHTMLEntities, setFormValues } from '@/utils';
import { ref, computed, onMounted, watch } from 'vue';
import Hint from "@components/hint.vue";

const telecomData = ref([]);
const plansAreAvailable = ref(true);
const savedFields = getFormValuesFromLocalStorage();
const phoneNumbers = ref(savedFields?.phoneNumbers);
const clientType = savedFields.activeTab === 'Business Client' ? 'company' : 'subscriber';
let delivery = savedFields[`${clientType}ShippingMethod`]
const oneTimeFeeLabel = ref('');
oneTimeFeeLabel.value = __('Shipping fees, connection fees, SIM card cost - one time');
const orderDropDown = ref(false);


const order_addons = ref([]);

const sim_count = savedFields.phoneNumbers.filter(phone => phone.sim_type === 'sim').length;
const esim_count = savedFields.phoneNumbers.filter(phone => phone.sim_type === 'esim').length;


if (checkIfOnlyEsim()){
	delivery = 'no_delivery';
}

const fetchPlansCTP = async () => {
    try {
        const response = await requestPlanCTP();
        telecomData.value = response;
    } catch (error) {
        console.error('Error fetching plans:', error);
    }
};

const fetchRegistrationFormFields = async () => {
    try {
        const response = await requestRegistrationFormFields();

		if(response.data){
			let productsWithFee = 0;
			const connection_fee = phoneNumbers.value.reduce((acc, value) => {
				const plan = telecomData.value.data?.find(item => item.plan_id === value.plan);
				const fee = plan.custom_fields?.app_telecom_plan_connection_fee;
				if(fee) {
					productsWithFee++;
					acc += parseFloat(fee);
				}
				return acc;
			}, 0);

			if(productsWithFee > 0) {
				order_addons.value.push({ name: __('Connection Fees'), price: connection_fee.toFixed(2), count: productsWithFee });
			}

			if (sim_count >= 1){
				const priceSim = (parseFloat(response.data.sim_price) * sim_count).toFixed(2);
				order_addons.value.push({ name: __('SIM Card'), price: priceSim, count: sim_count } );
			}

			if (esim_count >= 1){
				const priceEsim = (parseFloat(response.data.esim_price) * esim_count).toFixed(2);
				order_addons.value.push({ name: __('eSIM'), price: priceEsim, count: esim_count } );
			}

			if (delivery === 'fast') {
				order_addons.value.push({ name: __('HFD delivery'), price: response.data.fast_shipping_price, count: 1 } );
				savedFields.shippingPrice = response.data.fast_shipping_price;
				setFormValues(savedFields);
			}

			if (delivery === 'regular'){
				order_addons.value.push({ name: __('Israel Post delivery'), price: response.data.regular_shipping_price, count: 1 } );
				savedFields.shippingPrice = response.data.regular_shipping_price;
				setFormValues(savedFields);
			}

		}

    } catch (error) {
        console.error('Error fetching plans:', error);
    }
};

const getPlanPrice = (plan) => {
	if (!telecomData.value.data) return 0;
    const price = telecomData.value.data.find(item => item.plan_id === plan)?.montly_prices?.[0]?.price;
    return price;
};

const getPlanGB = (plan) => {
	if (!telecomData.value.data) return 'loading';
    const gb = telecomData.value.data.find(item => item.plan_id === plan)?.available_gigabyte
    return gb;
};

const getPlanMinutes = (plan) => {
	if (!telecomData.value.data) return 'loading';
    const minutes = telecomData.value.data.find(item => item.plan_id === plan)?.available_minutes;
    return minutes;
};

const getPlanTitle= (plan) => {
	if (!telecomData.value.data) return 'loading';
    const title = telecomData.value.data.find(item => item.plan_id === plan)?.title;
    return decodeHTMLEntities(title);
};


const getPlanPopUpText = (plan) => {
	if (!telecomData.value.data) return 'loading';
    const text = telecomData.value.data.find(item => item.plan_id === plan)?.popup_text
    return text;
};

const montlyFee = computed(() => {
    return phoneNumbers.value.reduce((acc, value) => {
        const price = getPlanPrice(value.plan);
        return (parseFloat(acc) + parseFloat(price)).toFixed(2);
    }, 0);
});

const planFees = computed(() => {
	return order_addons.value.reduce((acc, item) => parseFloat(acc) + parseFloat(item.price), 0).toFixed(2);
});

const toggleDropDown = () => {
	orderDropDown.value = !orderDropDown.value;
}

const simTypeFormat = (sim) => {
	if (sim === 'sim'){
		return 'SIM';
	}

	if (sim === 'esim'){
		return 'eSIM';
	}
}

const saveTotalPrice = () => {
    const totalPrice = parseFloat(montlyFee.value) + parseFloat(planFees.value);
    savedFields.totalPrice = totalPrice;
    setFormValues(savedFields);
};

onMounted(async() => {
    await fetchPlansCTP();
	await fetchRegistrationFormFields();

	saveTotalPrice();
});

watch([montlyFee, planFees], () => {
    saveTotalPrice();
});

</script>
<template>
	<div class="table">
		<header class="table__head">
			<h5>
				{{ __('My Order') }}
			</h5>
		</header><!-- /.table__head -->

		<div class="table__body">
			<div class="table__row table__row--numbers" v-if="phoneNumbers?.length" v-for="(number, index) in phoneNumbers" :index="index">
				<div class="table__col">
					<i class="ico ico--mobilephone"></i>

					<h5>
						{{ number.number }}
					</h5>
				</div><!-- /.table__col -->
				<div class="plan" v-if="number?.plan">
					<small class="plan__info">
						{{ getPlanTitle(number?.plan) }}
						<Hint>
							<span v-html="getPlanPopUpText(number?.plan)"></span>
						</Hint>
					</small>
					<!-- Needs to be updated -->
					<div class="plan__summary">
						<span>
							{{ getPlanGB(number?.plan) }}
						</span>
						<span v-if="getPlanMinutes(number.plan)">
							|
							{{ getPlanMinutes(number.plan) }}
						</span> 
						<span v-if="number.sim_type">
							|
							{{ simTypeFormat(number.sim_type) }}
						</span>
					</div>
					<div v-if="getPlanTitle(number?.plan).toLowerCase() === 'forever' || getPlanTitle(number?.plan).toLowerCase() === 'forever *5g*'">
						| חודש ראשון חינם
					</div>
					<span class="plan__price">₪{{ getPlanPrice(number?.plan) }}</span>
				</div>
			</div><!-- /.table__row -->

			<div class="table__row" v-if="plansAreAvailable">
				<div class="table__col">
					<h6>
						{{ __('Total monthly subscription fees') }}
					</h6>
				</div><!-- /.table__col -->

				<div class="table__col">
					<h4>₪{{ montlyFee }} </h4>
				</div><!-- /.table__col -->
			</div><!-- /.table__row -->

			<div class="table__row">
				<div class="table__col">
					<div class="dropdown dropdown-order" :class="{ 'is-active': orderDropDown }" @click="toggleDropDown">
						<div class="dropdown__head">
							<span>
								<h6>
									{{ oneTimeFeeLabel }}
								</h6>
							</span>
						</div><!-- /.dropdown__head -->
					</div><!-- /.dropdown -->
				</div>
				<div class="table__col">
					<h4>₪{{ planFees }} </h4>
				</div><!-- /.table__col -->

				<div class="row-dropdown">
					<div class="row-dropdown__body">
						<div class="row-dropdown__item" v-for="addon in order_addons" :key="addon.id" :class="{ 'is-active': orderDropDown }">
							<div>
								<span>
									{{ addon.count }}
								</span>
								X
								<span class="row-dropdown__item-title" dir="rtl">
									{{ addon.name }} 
								</span>
							</div>
							<div>
								<span>
									₪{{ addon.price }}
								</span>
							</div>
						</div><!-- /.row-dropdown__item -->
					</div><!-- /.row-dropdown__body -->
				</div><!-- /.row-dropdown -->

			</div><!-- /.table__row -->
		</div><!-- /.table__body -->
	</div><!-- /.table -->
</template>